import { useNavigate } from 'react-router-dom';
import { SortableTable, useSortState } from '../ui/SortableTable';
import type { ColumnDef } from '../ui/SortableTable';
import { COLORS } from '../../constants/theme';
import { formatNumber, formatEnumLabel, formatNetworthPerAcre } from '../../utils/format';
import { isProtected } from '../../utils/protection';
import { memberToProvinceSnapshot } from '../../utils/provinceSnapshot';
import type { KingdomMember } from '../../types/kingdom';

interface KingdomRosterTableProps {
  members: KingdomMember[];
  viewerProvinceId: string | null;
  currentTick: number;
  kingdomId: string;
  kingdomName: string;
  kingdomNw: number;
  kingdomLand: number;
}

export const RANK_ORDER: Record<string, number> = {
  PEASANT: 0, KNIGHT: 1, LORD: 2, BARON: 3, VISCOUNT: 4, COUNT: 5, MARQUIS: 6, DUKE: 7, PRINCE: 8,
};

/**
 * SF-169 Component 3: shared sortable roster table consumed by both `DesktopKingdomViewerShell`
 * and `Kingdom.tsx` — the piece that makes the anti-duplication goal real (both pages' own
 * hand-rolled tables and `RANK_ORDER` copies are gone, replaced by this one implementation).
 *
 * Columns: Province (sticky) / Race / Land / Net Worth / NW/Acre / Rank — 6 columns, column-for-
 * column parity with what `Kingdom.tsx`/the former `RosterTab.tsx` rendered.
 */
export function KingdomRosterTable({ members, viewerProvinceId, currentTick, kingdomId, kingdomName, kingdomNw, kingdomLand }: KingdomRosterTableProps) {
  const { sortState, handleSort } = useSortState();
  const navigate = useNavigate();

  // Clicking a row jumps straight to Target Finder's detail view (intel-gather + War
  // Room/Thievery/Magic shortcuts) using the snapshot data already in the roster — no re-fetch.
  function handleRowClick(member: KingdomMember) {
    const target = memberToProvinceSnapshot(member, { kingdomId, kingdomName, kingdomNw, kingdomLand });
    navigate('/world/targets', { state: { presetTarget: target, presetCurrentTick: currentTick } });
  }

  const columns: ColumnDef<KingdomMember>[] = [
    {
      key: 'provinceName',
      header: 'Province',
      sticky: true,
      sortable: true,
      sortAccessor: (m) => m.provinceName,
      render: (m) => {
        const protectedNow = isProtected(m.protectionExpiresAtTick, currentTick);
        // Precedence: monarch-red wins over protection-green (identity cue outranks status cue).
        const color = m.isMonarch ? COLORS.negative : protectedNow ? COLORS.positive : COLORS.text.primary;
        const isYou = m.provinceId === viewerProvinceId;
        return (
          <span>
            <span style={{ color, fontWeight: m.isMonarch || isYou ? 700 : 500 }}>
              {m.provinceName}
              {isYou && <span className="sr-only"> (you)</span>}
            </span>
            <span style={{ color: COLORS.text.muted, marginLeft: 4, fontSize: 11 }}>({m.location})</span>
            {/* SF-169 Recommended Change #7: non-color affordances (WCAG 1.4.1) — text markers
                alongside the color cue, not color alone. */}
            {m.isMonarch && (
              <span style={{ color: COLORS.negative, marginLeft: 6, fontSize: 11, fontWeight: 600 }}>♛ Monarch</span>
            )}
            {!m.isMonarch && protectedNow && (
              <span style={{ color: COLORS.positive, marginLeft: 6, fontSize: 11, fontWeight: 600 }}>Protected</span>
            )}
          </span>
        );
      },
    },
    {
      key: 'race',
      header: 'Race',
      sortable: true,
      sortAccessor: (m) => m.race,
      render: (m) => <span style={{ color: COLORS.text.tertiary }}>{formatEnumLabel(m.race)}</span>,
    },
    {
      key: 'land',
      header: 'Land',
      sortable: true,
      sortAccessor: (m) => m.land,
      render: (m) => <span style={{ fontVariantNumeric: 'tabular-nums' }}>{formatNumber(m.land)}</span>,
    },
    {
      key: 'networth',
      header: 'Net Worth',
      sortable: true,
      sortAccessor: (m) => m.networth,
      render: (m) => <span style={{ color: COLORS.gold, fontVariantNumeric: 'tabular-nums' }}>{formatNumber(m.networth)}</span>,
    },
    {
      key: 'nwpa',
      header: 'NW/Acre',
      sortable: true,
      // Raw-number accessor, deliberately distinct from the formatted display string — sorting on
      // the formatted string would compare "1,000" < "900" lexicographically (SF-169 Decision 8).
      sortAccessor: (m) => (m.land > 0 ? m.networth / m.land : 0),
      render: (m) => <span style={{ fontVariantNumeric: 'tabular-nums' }}>{formatNetworthPerAcre(m.networth, m.land)}</span>,
    },
    {
      key: 'rank',
      header: 'Rank',
      sortable: true,
      sortAccessor: (m) => RANK_ORDER[m.title] ?? 0,
      render: (m) => <span style={{ color: COLORS.text.tertiary }}>{formatEnumLabel(m.title)}</span>,
    },
  ];

  return (
    <div>
      <SortableTable
        columns={columns}
        rows={members}
        getRowKey={(m) => m.provinceId}
        sortState={sortState}
        onSort={handleSort}
        onRowClick={handleRowClick}
      />
      <div className="text-sm mt-2" style={{ color: COLORS.text.tertiary }}>
        Key:{' '}
        <span style={{ color: COLORS.positive }}>Green / "Protected"</span> = Protection{' '}
        <span style={{ color: COLORS.negative, fontWeight: 700 }}>Red / "♛ Monarch"</span> = Kingdom Monarch{' '}
        <span style={{ fontWeight: 700, color: COLORS.text.primary }}>Bold</span> = You
      </div>
    </div>
  );
}
